import React from "react";
import { BottomSheet as SpringBottomSheet } from "react-spring-bottom-sheet";
import { Capacitor } from "@capacitor/core";
import { AppLauncher } from "@capacitor/app-launcher";
import { useConfig } from "../../providers/config";
import { useTranslation } from "react-i18next";
import { App as AppPlugin } from "@capacitor/app";
import cx from "clsx";

interface UpdateBottomSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const UpdateBottomSheet = ({ isOpen, onDismiss }: UpdateBottomSheetProps) => {
  const config = useConfig();
  const { t } = useTranslation();

  const isIOS = Capacitor.getPlatform() === "ios";
  const latestVersion = isIOS ? config.latestIosVersion : config.latestAndroidVersion;

  const handleUpdate = async () => {
    const appInfo = await AppPlugin.getInfo();

    const toMarketUrl = isIOS
      ? `itms-apps://itunes.apple.com/app/${config.appIosId}`
      : `market://details?id=${appInfo.id}`;

    try {
      await AppLauncher.openUrl({ url: toMarketUrl });
    } catch {
      const webUrl = isIOS ? config.sharedData?.iosUrl : config.sharedData?.androidUrl;
      if (webUrl) window.open(webUrl, "_blank");
    }
  };

  return (
    <SpringBottomSheet
      open={isOpen}
      onDismiss={onDismiss}
      snapPoints={({ maxHeight }) => [maxHeight * 0.45]}
      expandOnContentDrag
      scrollLocking={false}
    >
      <div className="flex flex-col items-center px-6 py-4 gap-4">
        {isIOS ? <AppStoreIcon /> : <PlayMarketIcon />}

        <h2 className="text-xl font-bold text-gray-900 text-center">{t("New version available")}!</h2>

        <p className="text-sm text-gray-500 text-center leading-relaxed">
          {t("Please update the app to the latest version")}
          {latestVersion && <span className="font-semibold text-gray-700"> v{latestVersion}</span>}
        </p>

        <button
          onClick={handleUpdate}
          className="w-full py-3.5 rounded-xl font-semibold text-white text-base mt-1 bg-primary active:bg-primary/80 outline-none"
        >
          {isIOS ? t("Open App Store") : t("Open Google Play")}
        </button>

        <button
          onClick={onDismiss}
          className="w-full py-3.5 rounded-xl font-semibold bg-gray-200 text-gray-500 text-base outline-none"
        >
          {t("Later")}
        </button>
      </div>
    </SpringBottomSheet>
  );
};

const AppStoreIcon = () => (
  <div className="w-16 h-16 bg-gradient-to-br from-black to-black/80 rounded-2xl flex items-center justify-center shadow-lg">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 16.99 2.97 12.5 4.7 9.56C5.55 8.1 7.13 7.17 8.82 7.15C10.1 7.13 11.32 8.02 12.11 8.02C12.89 8.02 14.37 6.95 15.92 7.11C16.57 7.14 18.37 7.38 19.56 9.07C19.47 9.13 17.29 10.4 17.31 13.04C17.34 16.18 20.05 17.22 20.09 17.23C20.06 17.31 19.67 18.68 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"
        fill="white"
      />
    </svg>
  </div>
);

const PlayMarketIcon = () => (
  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.6 3 21.09 3 20.5Z" fill="white" />
      <path d="M16.81 15.12L6.05 21.34L14.54 12.85L16.81 15.12Z" fill="white" opacity="0.8" />
      <path
        d="M20.16 10.81C20.5 11.08 20.75 11.5 20.75 12C20.75 12.5 20.5 12.92 20.16 13.19L17.89 14.5L15.39 12L17.89 9.5L20.16 10.81Z"
        fill="white"
      />
      <path d="M6.05 2.66L16.81 8.88L14.54 11.15L6.05 2.66Z" fill="white" opacity="0.8" />
    </svg>
  </div>
);

export default UpdateBottomSheet;
